import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { DimPurchaseOrderField } from '~/common/types/cubes/DimPuchaseOrder';
import { useDateRange } from '~/components/DateRangeProvider';
import { MonthlyBarChart } from '~/components/MonthlyBarChart/MonthlyBarChart';
import { computeChangeRate, convertDateRangeToTimeDimension } from '~/util/cube';
import { getPrimaryValue } from '~/util/cube/getPrimaryValue';

interface PurchaseOrderVolumeWidgetProps {
  filters?: Filter[];
}
const formatChange = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);
const formatValueCompact = formatNumber(NumberFormatPreset.CURRENCY_COMPACT);

const WIDGET = 'PurchaseOrderVolumeWidget';

export const PurchaseOrderVolumeWidget = ({ filters }: PurchaseOrderVolumeWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.base, { keyPrefix: WIDGET });
  const [widgetRef, active] = useScrollIntoView({ hash: 'purchase-order-volume' });
  const { currentDateRange, referenceDateRange } = useDateRange();

  const measure = DimPurchaseOrderField.TOTAL_VALUE_NORMALIZED;

  const query = {
    measures: [measure],
    filters,
  };

  const tinyBarChart = useCubeQuery({
    ...query,
    timeDimensions: convertDateRangeToTimeDimension(DimPurchaseOrderField.CREATED_AT, currentDateRange, 'month'),
  });

  const { resultSet: resultSetCurrent, isLoading: loadingCurrent } = useCubeQuery({
    ...query,
    timeDimensions: convertDateRangeToTimeDimension(DimPurchaseOrderField.CREATED_AT, currentDateRange),
  });

  const { resultSet: resultSetPrevious, isLoading: loadingPrevious } = useCubeQuery({
    ...query,
    timeDimensions: convertDateRangeToTimeDimension(DimPurchaseOrderField.CREATED_AT, referenceDateRange),
  });

  const currentValue = getPrimaryValue(resultSetCurrent, measure) ?? 0;
  const previousValue = getPrimaryValue(resultSetPrevious, measure) ?? 0;
  const changeRate = computeChangeRate(currentValue, previousValue);
  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={loadingCurrent || loadingPrevious}
      title={t('title')}
      subTitle={t('subTitle')}
      valueTooltip={t('valueTooltip')}
      value={formatValueCompact(currentValue)}
      data-intercom-target={WIDGET}
      referenceValue={
        previousValue && (
          <QuickViewReferenceValue
            description={formatValueCompact(previousValue)}
            value={formatChange(changeRate)}
            dotColor={changeRate > 0 ? 'success' : 'error'}
          />
        )
      }
    >
      <MonthlyBarChart queryResult={tinyBarChart} caption={t('caption')} />
    </QuickViewWidget>
  );
};
